/**
 * Global modal: window.openSiteModal, window.closeSiteModal, window.openSiteConfirmModal
 * Depends on trapFocus / removeTrapFocus from global.js (load global.js before this file).
 */
(function () {
  const OPEN_CLASS = 'is-open';

  const root = () => document.querySelector('[data-site-modal-root]');
  const titleEl = () => document.getElementById('site-modal-title');
  const bodyEl = () => document.querySelector('[data-site-modal-body]');
  const panelEl = () => document.querySelector('[data-site-modal-root] .site-modal__panel');

  let lastFocus = null;
  let confirmResolver = null;
  let escapeHandler = null;
  let closing = false;
  let closeFallbackTimer = null;

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text == null ? '' : String(text);
    return div.innerHTML;
  }

  function isOpen() {
    const r = root();
    return r && r.classList.contains(OPEN_CLASS);
  }

  function finalizeClose() {
    const r = root();
    if (!r) return;
    const body = bodyEl();
    if (body) body.innerHTML = '';
    r.setAttribute('aria-hidden', 'true');
    r.removeAttribute('data-variant');
    r.removeAttribute('data-site-modal-close-on-outside');
    closing = false;
    document.dispatchEvent(new CustomEvent('site-modal:closed', { bubbles: true }));
  }

  /** Default true; opener may use data-site-modal-close-on-outside="false" */
  function parseCloseOnOutsideFromOpener(opener) {
    const v = opener.getAttribute('data-site-modal-close-on-outside');
    if (v == null || String(v).trim() === '') return true;
    const lower = String(v).trim().toLowerCase();
    if (lower === 'false' || lower === '0' || lower === 'no') return false;
    return true;
  }

  function parseClosePlacementFromOpener(opener) {
    const v = opener.getAttribute('data-site-modal-close-placement');
    if (v != null && String(v).trim().toLowerCase() === 'bottom') return 'bottom';
    return 'header';
  }

  function applyClosePlacement(opts, panel) {
    const placement = opts.closePlacement === 'bottom' ? 'bottom' : 'header';
    panel.setAttribute('data-close-placement', placement);
    const headerBtn = panel.querySelector('.site-modal__close--header');
    const footerWrap = panel.querySelector('[data-site-modal-footer-close-wrap]');
    if (!headerBtn || !footerWrap) return;
    if (placement === 'bottom') {
      headerBtn.setAttribute('hidden', '');
      footerWrap.removeAttribute('hidden');
    } else {
      headerBtn.removeAttribute('hidden');
      footerWrap.setAttribute('hidden', '');
    }
  }

  function getFocusCloseButton(panel) {
    const placement = panel.getAttribute('data-close-placement') || 'header';
    if (placement === 'bottom') {
      return panel.querySelector('.site-modal__close-footer');
    }
    return panel.querySelector('.site-modal__close--header');
  }

  function syncRootOptions(opts, r) {
    if (opts.variant) {
      r.setAttribute('data-variant', String(opts.variant));
    } else {
      r.removeAttribute('data-variant');
    }
    const allowOutside = opts.closeOnOutside !== false;
    r.setAttribute('data-site-modal-close-on-outside', allowOutside ? 'true' : 'false');
  }

  function handleCloseTransitionEnd(e) {
    const r = root();
    if (!r || e.target !== r || e.propertyName !== 'opacity') return;
    if (closeFallbackTimer) {
      clearTimeout(closeFallbackTimer);
      closeFallbackTimer = null;
    }
    r.removeEventListener('transitionend', handleCloseTransitionEnd);
    finalizeClose();
  }

  function closeSiteModal(immediate) {
    const r = root();
    const panel = panelEl();
    if (!r || !panel) return;

    const open = r.classList.contains(OPEN_CLASS);
    if (!open && !closing) return;

    if (escapeHandler) {
      document.removeEventListener('keydown', escapeHandler);
      escapeHandler = null;
    }
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(lastFocus);
    }
    lastFocus = null;
    document.body.classList.remove('overflow-hidden');

    if (confirmResolver) {
      confirmResolver(false);
      confirmResolver = null;
    }

    if (closeFallbackTimer) {
      clearTimeout(closeFallbackTimer);
      closeFallbackTimer = null;
    }
    r.removeEventListener('transitionend', handleCloseTransitionEnd);

    if (immediate || !open) {
      r.classList.remove(OPEN_CLASS);
      finalizeClose();
      return;
    }

    closing = true;
    r.classList.remove(OPEN_CLASS);

    closeFallbackTimer = setTimeout(function () {
      closeFallbackTimer = null;
      r.removeEventListener('transitionend', handleCloseTransitionEnd);
      finalizeClose();
    }, 520);

    r.addEventListener('transitionend', handleCloseTransitionEnd);
  }

  function applyModalContent(opts, title, body, panel, r) {
    const heading = opts.title != null ? String(opts.title).trim() : '';
    title.textContent = heading || 'Dialog';
    title.classList.toggle('visually-hidden', !heading);

    syncRootOptions(opts, r);

    body.innerHTML = '';

    if (opts.templateId) {
      const tpl = document.getElementById(opts.templateId);
      if (tpl && tpl.content) {
        body.appendChild(tpl.content.cloneNode(true));
      } else {
        console.warn('[site-modal] Template not found:', opts.templateId);
      }
    } else if (opts.html) {
      body.innerHTML = opts.html;
    } else if (opts.message != null && String(opts.message) !== '') {
      const p = document.createElement('p');
      p.className = 'site-modal__message';
      p.textContent = String(opts.message);
      body.appendChild(p);
    }

    applyClosePlacement(opts, panel);
  }

  function openSiteModal(options) {
    const opts = options || {};
    const r = root();
    const title = titleEl();
    const body = bodyEl();
    const panel = panelEl();
    const headerClose = panel && panel.querySelector('.site-modal__close--header');
    if (!r || !title || !body || !panel || !headerClose) {
      console.warn('[site-modal] Markup missing. Render snippet site-modal in theme.liquid.');
      return;
    }

    if (closing) {
      closeSiteModal(true);
    }

    if (r.classList.contains(OPEN_CLASS)) {
      applyModalContent(opts, title, body, panel, r);
      document.dispatchEvent(new CustomEvent('site-modal:opened', { bubbles: true, detail: opts }));
      return;
    }

    lastFocus = document.activeElement;
    applyModalContent(opts, title, body, panel, r);

    r.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    escapeHandler = function (e) {
      if (e.code === 'Escape' || e.key === 'Escape') {
        e.preventDefault();
        closeSiteModal(false);
      }
    };
    document.addEventListener('keydown', escapeHandler);

    requestAnimationFrame(function () {
      r.classList.add(OPEN_CLASS);
      requestAnimationFrame(function () {
        const focusClose = getFocusCloseButton(panel);
        if (typeof trapFocus === 'function' && focusClose) {
          trapFocus(panel, focusClose);
        } else if (focusClose) {
          focusClose.focus();
        }
      });
    });

    document.dispatchEvent(new CustomEvent('site-modal:opened', { bubbles: true, detail: opts }));
  }

  function openSiteConfirmModal(options) {
    const opts = options || {};
    const message = opts.message != null ? String(opts.message) : '';
    const confirmLabel = opts.confirmLabel != null ? String(opts.confirmLabel) : 'OK';
    const cancelLabel = opts.cancelLabel != null ? String(opts.cancelLabel) : 'Cancel';
    const variant = opts.variant === 'default' ? 'default' : 'warning';

    return new Promise(function (resolve) {
      confirmResolver = resolve;
      const html =
        '<p class="site-modal__message">' +
        escapeHtml(message) +
        '</p>' +
        '<div class="site-modal__actions">' +
        '<button type="button" class="site-modal__btn site-modal__btn--secondary" data-site-modal-close>' +
        escapeHtml(cancelLabel) +
        '</button>' +
        '<button type="button" class="site-modal__btn site-modal__btn--primary" data-site-modal-confirm>' +
        escapeHtml(confirmLabel) +
        '</button>' +
        '</div>';

      openSiteModal({
        title: opts.title != null ? opts.title : '',
        html: html,
        variant: variant,
        closeOnOutside: opts.closeOnOutside,
        closePlacement: opts.closePlacement,
      });
    });
  }

  function onDocumentClick(event) {
    const opener = event.target.closest('[data-site-modal-open]');
    if (opener) {
      event.preventDefault();
      const title = opener.getAttribute('data-site-modal-title') || '';
      const templateId = opener.getAttribute('data-site-modal-template') || '';
      const message = opener.getAttribute('data-site-modal-message') || '';
      const variant = opener.getAttribute('data-site-modal-variant') || 'default';
      const closeOnOutside = parseCloseOnOutsideFromOpener(opener);
      const closePlacement = parseClosePlacementFromOpener(opener);

      if (templateId) {
        openSiteModal({
          title: title,
          templateId: templateId,
          variant: variant,
          closeOnOutside: closeOnOutside,
          closePlacement: closePlacement,
        });
      } else if (message) {
        openSiteModal({
          title: title,
          message: message,
          variant: variant,
          closeOnOutside: closeOnOutside,
          closePlacement: closePlacement,
        });
      } else {
        openSiteModal({
          title: title,
          variant: variant,
          closeOnOutside: closeOnOutside,
          closePlacement: closePlacement,
        });
      }
      return;
    }

    if (event.target.closest('[data-site-modal-confirm]')) {
      if (confirmResolver) {
        const res = confirmResolver;
        confirmResolver = null;
        res(true);
      }
      closeSiteModal(false);
      return;
    }

    if (event.target.closest('[data-site-modal-close]')) {
      closeSiteModal(false);
      return;
    }

    /* Outside click: backdrop only covers the padding box; gutter clicks hit .site-modal itself. */
    const r = root();
    const panel = panelEl();
    if (r && panel && isOpen() && r.contains(event.target) && !panel.contains(event.target)) {
      if (r.getAttribute('data-site-modal-close-on-outside') !== 'false') {
        closeSiteModal(false);
      }
    }
  }

  window.openSiteModal = openSiteModal;
  window.closeSiteModal = function (immediate) {
    closeSiteModal(immediate === true);
  };
  window.openSiteConfirmModal = openSiteConfirmModal;

  document.addEventListener('click', onDocumentClick);
})();
